import { useState } from 'react';
import { PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { useQuery } from '@tanstack/react-query';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { bookEditionsQueryOptions } from '@/features/product/api/product.api';
import { getActiveStatusFilterField } from '@/utils/filterFields';
import { promoItemTypesQueryOptions, promoItemsInfiniteQueryOptions } from '../api/promotion.api';
import { getPromoItemColumns } from '../components/PromoColumns';
import { PromoItemFormModal } from '../components/PromoFormModals';
import {
  ACTIVE_ROWS_LOOKUP_PARAMS,
  ALL_ROWS_LOOKUP_PARAMS,
} from '../constants/promotion.constants';
import { usePromoCatalogueFilters } from '../hooks/usePromoCatalogueFilters';
import { useDeletePromoItem } from '../hooks/usePromoMutations';
import type { PromoItem } from '../types/promotion.types';
import {
  getPromoBookEditionFilterField,
  getPromoItemTypeFilterField,
} from '../utils/promotionFilterFields';
import { getPromoItemStatCards } from '../utils/promotionStatCards';

export function PromoItemListPage() {
  const controller = usePromoCatalogueFilters('items');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(promoItemsInfiniteQueryOptions(controller.params));
  const { data: types } = useQuery(promoItemTypesQueryOptions(ACTIVE_ROWS_LOOKUP_PARAMS));
  const { data: editions } = useQuery(bookEditionsQueryOptions(ALL_ROWS_LOOKUP_PARAMS));

  const [editing, setEditing] = useState<PromoItem | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PromoItem | null>(null);

  const remove = useDeletePromoItem(() => setPendingDelete(null));

  const columns = getPromoItemColumns(controller.search.search, {
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
    onDelete: setPendingDelete,
  });

  return (
    <CatalogueListLayout
      title="Promotional Item List"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search by name or code',
      }}
      filters={controller}
      filterFields={[
        getActiveStatusFilterField(),
        getPromoItemTypeFilterField(types?.data),
        getPromoBookEditionFilterField(editions?.data),
      ]}
      stats={{
        cards: getPromoItemStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      addRequires={PROMOTION_PERMISSIONS.ITEM_LIST_CREATE}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Promotional Item"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the items"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No promotional item matches"
          {...loadMoreProps}
        />
      )}

      <PromoItemFormModal item={editing} open={isFormOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete item?"
        description={
          pendingDelete === null
            ? ''
            : `"${pendingDelete.name}" will be deleted. You cannot undo this. If the item was already handed out, you cannot delete it. Make it inactive instead.`
        }
        confirmLabel="Delete"
        confirmVariant="destructive"
        isPending={remove.isPending}
        pendingLabel="Deleting..."
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </CatalogueListLayout>
  );
}
